/**
 * PurchaseOrderDetailPage — Issue #331.
 *
 * 采购订单详情：明细 + 收货操作 + 关闭。
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  hasPermission,
  PURCHASE_ORDER_PERMISSIONS,
} from '@/lib/procurementPermissions';
import {
  usePurchaseOrderDetail,
  usePurchaseOrderActions,
  type PurchaseOrderItemDto,
} from '@/hooks/usePurchaseOrder';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  submitted: '已提交',
  approved: '已批准',
  partially_received: '部分收货',
  received: '已收货',
  closed: '已关闭',
  cancelled: '已取消',
};

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

interface LineForm {
  itemId: string;
  quantity: string;
  batchNo: string;
  expiryDate: string;
}

interface ReceiveDialogProps {
  open: boolean;
  onClose: () => void;
  items: ReadonlyArray<PurchaseOrderItemDto>;
  expectedVersion: number;
  onSubmit: (lines: LineForm[]) => Promise<void>;
  loading: boolean;
}

function ReceiveDialog({
  open,
  onClose,
  items,
  expectedVersion: _expectedVersion,
  onSubmit,
  loading,
}: ReceiveDialogProps) {
  const [lines, setLines] = useState<LineForm[]>([]);
  if (!open) return null;
  void _expectedVersion;

  function toggleLine(item: PurchaseOrderItemDto) {
    setLines((cur) => {
      const idx = cur.findIndex((l) => l.itemId === item.id);
      if (idx >= 0) return cur.filter((l) => l.itemId !== item.id);
      const outstanding = new Decimal(item.quantity).minus(
        new Decimal(item.receivedQuantity),
      );
      return [
        ...cur,
        {
          itemId: item.id,
          quantity: outstanding.toFixed(6).replace(/\.?0+$/, ''),
          batchNo: '',
          expiryDate: '',
        },
      ];
    });
  }
  function updateLine(itemId: string, patch: Partial<LineForm>) {
    setLines((cur) =>
      cur.map((l) => (l.itemId === itemId ? { ...l, ...patch } : l)),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      window.alert('请至少选择一条收货明细');
      return;
    }
    await onSubmit(lines);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="mb-4 text-lg font-semibold">采购收货</h2>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <p className="text-sm text-gray-600">
            勾选要收货的条目；关联 consumable 的条目必须填写批次号和效期。
          </p>
          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-2 text-left">选择</th>
                  <th className="px-2 py-2 text-left">物料</th>
                  <th className="px-2 py-2 text-right">订购</th>
                  <th className="px-2 py-2 text-right">已收</th>
                  <th className="px-2 py-2 text-left">本次数量</th>
                  <th className="px-2 py-2 text-left">批次号</th>
                  <th className="px-2 py-2 text-left">效期</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => {
                  const line = lines.find((l) => l.itemId === it.id);
                  const needsBatch = !!it.consumableId;
                  return (
                    <tr key={it.id}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!!line}
                          onChange={() => toggleLine(it)}
                        />
                      </td>
                      <td className="px-2 py-2 text-xs">
                        {it.materialName}
                        {it.specification ? ` / ${it.specification}` : ''}
                      </td>
                      <td className="px-2 py-2 text-right text-xs">{it.quantity}</td>
                      <td className="px-2 py-2 text-right text-xs">
                        {it.receivedQuantity}
                      </td>
                      <td className="px-2 py-2">
                        {line && (
                          <input
                            required
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]+(\.[0-9]+)?"
                            className="w-24 rounded border border-gray-300 px-1 py-0.5 text-xs"
                            value={line.quantity}
                            onChange={(e) =>
                              updateLine(it.id, { quantity: e.target.value })
                            }
                          />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {line && (
                          <input
                            required={needsBatch}
                            placeholder={needsBatch ? '必填' : '可选'}
                            className="w-32 rounded border border-gray-300 px-1 py-0.5 text-xs"
                            value={line.batchNo}
                            onChange={(e) =>
                              updateLine(it.id, { batchNo: e.target.value })
                            }
                          />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {line && (
                          <input
                            required={needsBatch}
                            type="date"
                            className="rounded border border-gray-300 px-1 py-0.5 text-xs"
                            value={line.expiryDate}
                            onChange={(e) =>
                              updateLine(it.id, { expiryDate: e.target.value })
                            }
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '确认收货'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PurchaseOrderDetailPage() {
  const [, params] = useRoute('/procurement/purchase-orders/:id');
  const [, navigate] = useLocation();
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, PURCHASE_ORDER_PERMISSIONS.READ);
  const canReceive = hasPermission(
    permissions,
    PURCHASE_ORDER_PERMISSIONS.RECEIVE,
  );
  const canApprove = hasPermission(
    permissions,
    PURCHASE_ORDER_PERMISSIONS.APPROVE,
  );

  const { data, loading, error, fetchDetail } = usePurchaseOrderDetail();
  const {
    receive,
    close,
    error: actionError,
    loading: actionLoading,
  } = usePurchaseOrderActions();

  const [receiveOpen, setReceiveOpen] = useState(false);

  useEffect(() => {
    if (canRead && params?.id) {
      void fetchDetail(params.id);
    }
  }, [canRead, params?.id]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看采购订单的权限。</div>
    );
  }
  if (loading) {
    return <div className="p-6 text-sm text-gray-500">加载中…</div>;
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-500">未找到该采购订单。</div>;
  }

  async function handleReceiveSubmit(lines: LineForm[]) {
    if (!data) return;
    const ok = await receive(data.id, {
      expectedVersion: data.version,
      lines: lines.map((l) => ({
        itemId: l.itemId,
        quantity: l.quantity,
        batchNo: l.batchNo || undefined,
        expiryDate: l.expiryDate || undefined,
      })),
    });
    if (ok) {
      setReceiveOpen(false);
      void fetchDetail(data.id);
    }
  }

  async function onClose() {
    if (!data) return;
    const ok = await close(data.id, { expectedVersion: data.version });
    if (ok) void fetchDetail(data.id);
  }

  const status = data.status;
  const canReceiveNow =
    canReceive && (status === 'approved' || status === 'partially_received');
  const canCloseNow = canApprove && status === 'received';

  return (
    <div className="space-y-4 p-6">
      <button
        onClick={() => navigate('/procurement/purchase-orders')}
        className="text-sm text-blue-600 hover:underline"
      >
        ← 返回列表
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold font-mono">{data.orderNo}</h1>
          <p className="text-sm text-gray-600">
            状态：{STATUS_LABELS[status] ?? status} · 合计：
            {fmtCny(data.totalAmount)} · 供应商：{data.supplierId}
          </p>
        </div>
        <div className="flex gap-2">
          {canReceiveNow && (
            <button
              onClick={() => setReceiveOpen(true)}
              className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            >
              收货
            </button>
          )}
          {canCloseNow && (
            <button
              onClick={() => void onClose()}
              disabled={actionLoading}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm"
            >
              关闭订单
            </button>
          )}
        </div>
      </div>

      {(error || actionError) && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {(error ?? actionError)!.message}
        </div>
      )}

      <div className="rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">物料</th>
              <th className="px-3 py-2 text-left">consumable</th>
              <th className="px-3 py-2 text-left">服务目录</th>
              <th className="px-3 py-2 text-right">数量</th>
              <th className="px-3 py-2 text-right">单价</th>
              <th className="px-3 py-2 text-right">小计</th>
              <th className="px-3 py-2 text-right">已收</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map((it) => (
              <tr key={it.id}>
                <td className="px-3 py-2 text-xs">{it.lineNo}</td>
                <td className="px-3 py-2 text-xs">
                  {it.materialName}
                  {it.specification ? ` / ${it.specification}` : ''}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {it.consumableId ?? '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {it.serviceCatalogId ?? '—'}
                </td>
                <td className="px-3 py-2 text-right text-xs">{it.quantity}</td>
                <td className="px-3 py-2 text-right text-xs">{it.unitPrice}</td>
                <td className="px-3 py-2 text-right text-xs">{it.lineAmount}</td>
                <td className="px-3 py-2 text-right text-xs">
                  {it.receivedQuantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReceiveDialog
        key={receiveOpen ? 'open' : 'closed'}
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        items={data.items}
        expectedVersion={data.version}
        onSubmit={handleReceiveSubmit}
        loading={actionLoading}
      />
    </div>
  );
}
